"use client";

/**
 * Schedule Day Stepper
 * Steps through schedule days one at a time, in the shape of the Fetch team day stepper: a
 * bordered pill with previous and next buttons around the day ("Today, Wed, Dec 9"), plus a
 * "Back to today" button when today is an event day and another day is showing.
 */

import type React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { FetchButton } from "@/fetch-ui/FetchButton";
import { FetchIconButton } from "@/fetch-ui/FetchIconButton";

import { formatScheduleDayStepLabel } from "../../../../_logic/utils/schedule-display-format";

interface ScheduleDayStepperProps {
  /** The days to step through, in order (`YYYY-MM-DD`). */
  days: string[];
  /** The day showing now. */
  selectedDate: string;
  /** Today in the event's zone, or null before it resolves on the client. */
  todayStr: string | null;
  onChange: (date: string) => void;
}

/**
 * Renders the day stepper.
 *
 * @param props - See ScheduleDayStepperProps.
 * @returns The stepper element.
 */
export function ScheduleDayStepper({
  days,
  selectedDate,
  todayStr,
  onChange,
}: ScheduleDayStepperProps): React.ReactElement {
  const index = days.indexOf(selectedDate);
  const isFirst = index <= 0;
  const isLast = index === days.length - 1;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white p-0.5">
        <FetchIconButton
          icon={<ChevronLeft />}
          label="Previous day"
          size="md"
          disabled={isFirst}
          onClick={() => onChange(days[index - 1])}
          className="rounded-full"
        />
        <h3 aria-live="polite" className="min-w-40 text-center text-base font-semibold tabular-nums text-gray-900">
          {formatScheduleDayStepLabel(selectedDate, selectedDate === todayStr)}
        </h3>
        <FetchIconButton
          icon={<ChevronRight />}
          label="Next day"
          size="md"
          disabled={isLast}
          onClick={() => onChange(days[index + 1])}
          className="rounded-full"
        />
      </div>
      {todayStr !== null && days.includes(todayStr) && selectedDate !== todayStr && (
        <FetchButton variant="ghost" color="neutral" size="sm" onClick={() => onChange(todayStr)}>
          Back to today
        </FetchButton>
      )}
    </div>
  );
}
